import axios from 'axios';
import ToastHelper from "../general/helper/toastHelper";
import queryString from 'query-string';
import PreferenceKey from "../general/constant/preferenceKey";
import UserHelper from "../general/helper/userHelper";

const sTag = '[AxiosClient]';

const axiosClient = axios.create({
    baseURL: process.env.REACT_APP_BASE_URL,
    headers: {
        'content-type': 'application/json',
    },
    paramsSerializer: (params) => queryString.stringify(params),
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

axiosClient.interceptors.request.use(async (config) => {
    const token = localStorage.getItem(PreferenceKey.accessToken);
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

axiosClient.interceptors.response.use(
    (response) => {
        if (response.headers['session-token']) {
            axiosClient.defaults.headers.common['session-token'] = response.headers['session-token'];
        }
        return response?.data || response;
    },
    async (error) => {
        const originalRequest = error.config;
        const response = error.response;

        if (response && (response.status === 401 || response.status === 403)) {
            // Ngăn lặp vô hạn khi /refresh-token cũng trả về lỗi
            if (originalRequest._retry) {
                UserHelper.signOut();
                window.location.href = '/auth/sign-in';
                return;
            }

            originalRequest._retry = true;

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers['Authorization'] = 'Bearer ' + token;
                            resolve(axiosClient(originalRequest));
                        },
                        reject: (err) => reject(err)
                    });
                });
            }

            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem(PreferenceKey.refreshToken);
                const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/auth/refresh-token`, {
                    refreshToken,
                });



                const { accessToken,  refreshToken: newRefreshToken } = res.data;
                const {token} = accessToken;
 
                localStorage.setItem(PreferenceKey.accessToken, token);

                updateAccessToken(token);
                processQueue(null, token);

                originalRequest.headers['Authorization'] = 'Bearer ' + token;
                return axiosClient(originalRequest);
            } catch (err) {
                processQueue(err, null);
                UserHelper.signOut();
                window.location.href = '/auth/sign-in';
                return;
            } finally {
                isRefreshing = false;
            }
        }

        // Xử lý lỗi khác
        let errorMessage = null;
        if (response && response.data) {
            const { result, reason, detail } = response.data;
            if (result === 'failed') {
                errorMessage = reason || detail;
            }
        }
        if (errorMessage) {
            ToastHelper.showError(errorMessage);
        }

        throw error;
    }
);

// ---- Hàm tiện ích
const updateAxiosBaseURL = (baseUrl) => {
    axiosClient.defaults.baseURL = baseUrl;
};

const updateAxiosApiKey = (apiKey) => {
    axiosClient.defaults.headers.common['apiSecret'] = apiKey;
};

const removeAxiosApiKey = () => {
    delete axiosClient.defaults.headers.common['apiSecret'];
};

const updateAccessToken = (accessToken) => {
    axiosClient.defaults.headers.common['Authorization'] = "Bearer " + accessToken;
};

const removeAccessToken = () => {
    delete axiosClient.defaults.headers.common['Authorization'];
};

// ---- Khởi tạo accessToken ban đầu
(() => {
    const accessToken = localStorage.getItem(PreferenceKey.accessToken);
    if (accessToken) {
        updateAccessToken(accessToken);
    } else {
        UserHelper.signOut();
    }
})();

export {
    updateAxiosApiKey,
    removeAxiosApiKey,
    updateAxiosBaseURL,
    updateAccessToken,
    removeAccessToken
};

export default axiosClient;
